// CustomMarkdownEditor.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Grid,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  Button
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough';
import CodeIcon from '@mui/icons-material/Code';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import UndoIcon from '@mui/icons-material/Undo';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import Picker from 'emoji-picker-react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { TypeAnimation } from 'react-type-animation'; // Import TypeAnimation

import BusinessIcon from '@mui/icons-material/Business';
import RemoveIcon from '@mui/icons-material/Remove';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import SpellcheckIcon from '@mui/icons-material/Spellcheck';
import ShortTextIcon from '@mui/icons-material/ShortText';
import SubjectIcon from '@mui/icons-material/Subject';
import TranslateIcon from '@mui/icons-material/Translate';
import CircularProgress from '@mui/material/CircularProgress';
import axios from 'axios';
import { GENAI_ENHANCE_TEXT_ENDPOINT } from '../../config/urls';

const CustomMarkdownEditor = ({ value, onChange, maxLength, disabled=false, onKeyDown=() => {} }) => {
    const [text, setText] = useState(value || '');
    const [history, setHistory] = useState([value || '']);
    const [historyIndex, setHistoryIndex] = useState(0);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const textareaRef = useRef(null);

    // First, add these to your state variables
    const [enhancedText, setEnhancedText] = useState('');
    const [isEnhancing, setIsEnhancing] = useState(false);
    const [showEnhanced, setShowEnhanced] = useState(false);
    const [enhanceOption, setEnhanceOption] = useState('professional');
    const [enhanceError, setEnhanceError] = useState('');
    const [animationComplete, setAnimationComplete] = useState(false);
    const permissionsFromStorage = JSON.parse(localStorage.getItem('permissions') || '[]');
    // Define the specific permission required for text enhancement
    const ENHANCE_PERMISSION = 'gen_ai_live_agent.change';

    // Add this menu state for the enhance options
    const [enhanceMenuAnchor, setEnhanceMenuAnchor] = useState(null);
    
    const hasEnhancePermission = permissionsFromStorage.includes(ENHANCE_PERMISSION);

    // Define consistent text styling for all text areas
    const textAreaStyle = {
        width: '100%',
        height: '150px',
        padding: '12px',
        fontFamily: 'monospace',
        fontSize: '14px', // Adding consistent font size
        border: '1px solid #ddd',
        borderTop: 'none',
        resize: 'vertical',
        outline: 'none',
        whiteSpace: 'pre-wrap',
        lineHeight: '1.5', // Adding consistent line height
    };

    // Add this function to cancel enhancement
    const handleCancelEnhancement = () => {
        setShowEnhanced(false);
        setEnhancedText('');
        setAnimationComplete(false);
    };
    
    // Add this function to replace the original text with enhanced text
    const handleReplaceWithEnhanced = (maxLength) => {
        // Save current text to history for undo
        const newHistory = history.slice(0, historyIndex + 1);
        newHistory.push(text);
        setHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
    
        // Limit enhancedText to 1020 characters
        const limitedText = enhancedText.slice(0, maxLength);
    
        // Replace text with enhanced version
        setText(limitedText);
        onChange(limitedText);
    
        // Close enhanced preview
        setShowEnhanced(false);
        setEnhancedText('');
        setAnimationComplete(false);
    };
    
    // Add this function to close the enhance menu
    const handleCloseEnhanceMenu = () => {
        setEnhanceMenuAnchor(null);
    };

    // Add this function to select an enhancement option
    const handleSelectEnhanceOption = (option) => {
        setEnhanceOption(option);
        handleCloseEnhanceMenu();
        performEnhancement(option);
    };

    // Add this function to perform the text enhancement
    const performEnhancement = async (option) => {
        if (!text.trim()) return;
        
        setIsEnhancing(true);
        setEnhanceError('');
        setAnimationComplete(false);
        
        try {
            const response = await axios.post(`${GENAI_ENHANCE_TEXT_ENDPOINT}`, {
                prompt: text,
                tone: option
            }, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            
            setEnhancedText(response.data.enhanced_text);
            setShowEnhanced(true);
        } catch (error) {
            console.error('Error enhancing text:', error);
            setEnhanceError(error.response?.data?.error || 'Failed to enhance text');
        } finally {
            setIsEnhancing(false);
        }
    };

    // Handle animation completion
    const handleAnimationComplete = () => {
        setAnimationComplete(true);
    };

    const handleKeyDownInternal = (e) => {
        // Check for Ctrl+Z (Undo)
        if (e.ctrlKey && e.key === 'z') {
            e.preventDefault(); // Prevent the browser's default undo behavior
            handleUndo();
        }
        
        // Special handling for Enter key
        if (e.key === 'Enter') {
            if (!e.shiftKey) {
                // For regular Enter, we'll delegate to parent's onKeyDown
                if (onKeyDown) {
                    onKeyDown(e);
                }
            }
            // When Shift+Enter is pressed, the default behavior (new line) will proceed
        }
        
        // Pass any other keys to parent handler if defined
        else if (onKeyDown) {
            onKeyDown(e);
        }
    };

    useEffect(() => {
        // Function to handle keyboard shortcuts
        const handleKeyDown = (e) => {
            // Check for Ctrl+Z (Undo)
            if (e.ctrlKey && e.key === 'z') {
                e.preventDefault(); // Prevent the browser's default undo behavior
                handleUndo();
            }
        };
    
        // Add event listener to the textarea
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.addEventListener('keydown', handleKeyDown);
        }
    
        // Cleanup: remove event listener when component unmounts
        return () => {
            if (textarea) {
                textarea.removeEventListener('keydown', handleKeyDown);
            }
        };
    }, [historyIndex]); // Dependency on historyIndex ensures the latest state is used

    useEffect(() => {
        // This effect syncs the component's internal state with the parent's value prop
        if (value !== text) {
            setText(value);
            
            // Update history if this is a genuine update (e.g., clearing the field)
            if (!history.includes(value)) {
                const newHistory = [...history, value];
                setHistory(newHistory);
                setHistoryIndex(newHistory.length - 1);
            }
        }
    }, [value]);

    const RichTextArea = ({ value, onChange, maxLength, textareaRef }) => {
        // Function to convert markdown to HTML for display
        const convertMarkdownToDisplayHtml = (content) => {
            if (!content) return '';
            
            let html = content;
            
            // We'll preserve the markdown marks but add styling
            // Bold
            html = html.replace(/\*([^*]+)\*/g, '<span class="markdown-bold">*$1*</span>');
            
            // Italic
            html = html.replace(/_([^_]+)_/g, '<span class="markdown-italic">_$1_</span>');
            
            // Strikethrough
            html = html.replace(/~([^~]+)~/g, '<span class="markdown-strikethrough">~$1~</span>');
            
            // Monospace
            html = html.replace(/```([^`]+)```/g, '<span class="markdown-mono">```$1```</span>');
            html = html.replace(/`([^`]+)`/g, '<span class="markdown-inline-code">`$1`</span>');
            
            // Lists
            html = html.replace(/^(\* .+)$/gm, '<span class="markdown-list-item">$&</span>');
            html = html.replace(/^(- .+)$/gm, '<span class="markdown-list-item">$&</span>');
            html = html.replace(/^(\d+\. .+)$/gm, '<span class="markdown-list-item">$&</span>');
            
            // Quotes
            html = html.replace(/^(> .+)$/gm, '<span class="markdown-quote">$&</span>');
            
            return html;
        };

        // Style definitions
        const styles = `
            .markdown-bold { font-weight: bold; color: #000; }
            .markdown-italic { font-style: italic; color: #000; }
            .markdown-strikethrough { text-decoration: line-through; color: #000; }
            .markdown-mono, .markdown-inline-code { font-family: monospace; background-color: #f0f0f0; padding: 0 2px; color: #000; }
            .markdown-list-item { display: block; color: #000; }
            .markdown-quote { display: block; border-left: 3px solid #ccc; padding-left: 10px; color: #666; }
        `;

        // The editor container needs to handle input and maintain selection
        return (
            <div className="rich-text-editor-container" style={{ position: 'relative' }}>
            <style>{styles}</style>
            <div
                className="rich-text-display"
                style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                padding: '12px',
                fontFamily: 'monospace',
                fontSize: '14px',
                lineHeight: '1.5',
                whiteSpace: 'pre-wrap',
                overflow: 'auto',
                pointerEvents: 'none',
                backgroundColor: 'transparent',
                zIndex: 1
                }}
                dangerouslySetInnerHTML={{ __html: convertMarkdownToDisplayHtml(value) }}
            />
            <textarea
                ref={textareaRef}
                value={value}
                onChange={onChange}
                style={{
                ...textAreaStyle,
                color: 'transparent',
                caretColor: 'black',
                backgroundColor: 'transparent',
                position: 'relative',
                zIndex: 2
                }}
                spellCheck={true}
                maxLength={maxLength}
            />
            </div>
        );
    };

    // Handle text changes
    const handleChange = (e) => {
        const newText = e.target.value;
        
        if (newText.length <= maxLength) {
            setText(newText);
            
            // Add to history for undo functionality
            if (newText !== history[historyIndex]) {
                const newHistory = history.slice(0, historyIndex + 1);
                newHistory.push(newText);
                setHistory(newHistory);
                setHistoryIndex(newHistory.length - 1);
            }
            
            // Call the parent onChange
            onChange(newText);
        }
    };

    // Handle formatting button clicks
    const applyFormatting = (type) => {
        if (!textareaRef.current) return;
        
        const textarea = textareaRef.current;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = text.substring(start, end);
        
        let formattedText = '';
        let newCursorPos = 0;
        let cursorAdjustment = 0;
        
        // Helper function to check if text already has formatting
        const hasFormatting = (text, format) => {
            switch(format) {
                case 'bold':
                    return text.startsWith('*') && text.endsWith('*');
                case 'italic':
                    return text.startsWith('_') && text.endsWith('_');
                case 'strikethrough':
                    return text.startsWith('~') && text.endsWith('~');
                case 'monospace':
                    return text.startsWith('```') && text.endsWith('```');
                case 'inline-code':
                    return text.startsWith('`') && text.endsWith('`');
                default:
                    return false;
            }
        };
        
        // Check if the selected text already has the formatting we're trying to apply
        const alreadyFormatted = hasFormatting(selectedText, type);
        
        if (alreadyFormatted) {
            // Remove formatting
            switch (type) {
                case 'bold':
                    formattedText = selectedText.substring(1, selectedText.length - 1);
                    cursorAdjustment = -2; // Adjust cursor for removed asterisks
                    break;
                case 'italic':
                    formattedText = selectedText.substring(1, selectedText.length - 1);
                    cursorAdjustment = -2; // Adjust cursor for removed underscores
                    break;
                case 'strikethrough':
                    formattedText = selectedText.substring(1, selectedText.length - 1);
                    cursorAdjustment = -2; // Adjust cursor for removed tildes
                    break;
                case 'monospace':
                    formattedText = selectedText.substring(3, selectedText.length - 3);
                    cursorAdjustment = -6; // Adjust cursor for removed backticks
                    break;
                case 'inline-code':
                    formattedText = selectedText.substring(1, selectedText.length - 1);
                    cursorAdjustment = -2; // Adjust cursor for removed backticks
                    break;
                default:
                    formattedText = selectedText;
            }
        } else {
            // Add formatting
            switch (type) {
                case 'bold':
                    formattedText = `*${selectedText}*`;
                    newCursorPos = 1;
                    cursorAdjustment = 2; // Adjust cursor for added asterisks
                    break;
                case 'italic':
                    formattedText = `_${selectedText}_`;
                    newCursorPos = 1;
                    cursorAdjustment = 2; // Adjust cursor for added underscores
                    break;
                case 'strikethrough':
                    formattedText = `~${selectedText}~`;
                    newCursorPos = 1;
                    cursorAdjustment = 2; // Adjust cursor for added tildes
                    break;
                case 'monospace':
                    formattedText = `\`\`\`${selectedText}\`\`\``;
                    newCursorPos = 3;
                    cursorAdjustment = 6; // Adjust cursor for added backticks
                    break;
                case 'inline-code':
                    formattedText = `\`${selectedText}\``;
                    newCursorPos = 1;
                    cursorAdjustment = 2; // Adjust cursor for added backticks
                    break;
                case 'bullet-list':
                    formattedText = selectedText
                    .split('\n')
                    .map(line => line ? `- ${line}` : line)
                    .join('\n');
                    newCursorPos = 2;
                    break;
                case 'number-list':
                    formattedText = selectedText
                    .split('\n')
                    .map((line, i) => line ? `${i + 1}. ${line}` : line)
                    .join('\n');
                    newCursorPos = 3;
                    break;
                case 'quote':
                    formattedText = selectedText
                    .split('\n')
                    .map(line => line ? `> ${line}` : line)
                    .join('\n');
                    newCursorPos = 2;
                    break;
                default:
                    formattedText = selectedText;
            }
        }
        
        // Calculate the new text length and check against maxLength
        const newText = text.substring(0, start) + formattedText + text.substring(end);
        
        // ** NEW: Check if the formatted text exceeds maxLength **
        if (newText.length > maxLength) {
            console.warn('Formatting would exceed maximum length limit');
            return; // Prevent the formatting if it would exceed the limit
        }
        
        setText(newText);
        
        // Add to history
        const newHistory = history.slice(0, historyIndex + 1);
        newHistory.push(newText);
        setHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
        
        // Call the parent onChange
        onChange(newText);
        
        // Set focus back to the textarea and position cursor
        setTimeout(() => {
            textarea.focus();
            if (start === end) {
                // If no text was selected, position cursor between markers
                const newPos = start + newCursorPos;
                textarea.setSelectionRange(newPos, newPos);
            } else {
                // If text was selected, select the newly formatted text
                const newStart = start;
                const newEnd = start + formattedText.length;
                textarea.setSelectionRange(newStart, newEnd);
            }
        }, 0);
    };

    // Handle undo
    const handleUndo = () => {
        if (historyIndex > 0) {
            setHistoryIndex(historyIndex - 1);
            const previousText = history[historyIndex - 1];
            setText(previousText);
            onChange(previousText);
        }
    };

    // Handle enhance click
    const handleClickEnhance = (event) => {
        if (!hasEnhancePermission) {
          return; // Do nothing if no permission
        }
        setEnhanceMenuAnchor(event.currentTarget);
    };

    // Handle emoji selection
    const onEmojiClick = (emojiData, event) => {
        // The API of emoji-picker-react has changed, emoji data is now the first parameter
        // Check if we have valid emoji data
        if (!emojiData || !emojiData.emoji) {
            console.error("Invalid emoji data:", emojiData);
            return;
        }
        
        const emoji = emojiData.emoji;
        const textarea = textareaRef.current;
        
        if (!textarea) {
            console.error("Textarea reference is undefined");
            return;
        }
        
        const start = textarea.selectionStart;
        
        const newText = 
            text.substring(0, start) + 
            emoji + 
            text.substring(start);
        
        // Check if newText is within maxLength before updating state
        if (maxLength && newText.length > maxLength) {
            console.warn("Adding emoji would exceed maxLength");
            return;
        }
        
        setText(newText);
        
        // Add to history
        const newHistory = history.slice(0, historyIndex + 1);
        newHistory.push(newText);
        setHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
        
        // Call the parent onChange
        onChange(newText);
        
        // Close emoji picker
        setShowEmojiPicker(false);
        
        // Set focus back to textarea
        setTimeout(() => {
            textarea.focus();
            const newPos = start + emoji.length;
            textarea.setSelectionRange(newPos, newPos);
        }, 0);
    };

    return (
        <div style={{ width: '100%' }}>
        {/* Toolbar */}
        <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            padding: '8px',
            backgroundColor: '#f5f5f5',
            borderTopLeftRadius: '4px',
            borderTopRightRadius: '4px',
            borderBottom: '1px solid #ddd'
        }}>
            <Tooltip title="Bold (*text*)">
            <IconButton onClick={() => applyFormatting('bold')}>
                <FormatBoldIcon />
            </IconButton>
            </Tooltip>
            
            <Tooltip title="Italic (_text_)">
            <IconButton onClick={() => applyFormatting('italic')}>
                <FormatItalicIcon />
            </IconButton>
            </Tooltip>
            
            <Tooltip title="Strikethrough (~text~)">
            <IconButton onClick={() => applyFormatting('strikethrough')}>
                <FormatStrikethroughIcon />
            </IconButton>
            </Tooltip>
            
            <Tooltip title="Monospace (```text```)">
            <IconButton onClick={() => applyFormatting('monospace')}>
                <CodeIcon />
            </IconButton>
            </Tooltip>
            
            <Divider orientation="vertical" flexItem style={{ margin: '0 8px' }} />
            
            <Tooltip title="Bulleted List (* text)">
            <IconButton onClick={() => applyFormatting('bullet-list')}>
                <FormatListBulletedIcon />
            </IconButton>
            </Tooltip>
            
            <Tooltip title="Numbered List (1. text)">
            <IconButton onClick={() => applyFormatting('number-list')}>
                <FormatListNumberedIcon />
            </IconButton>
            </Tooltip>
            
            <Tooltip title="Quote (> text)">
            <IconButton onClick={() => applyFormatting('quote')}>
                <FormatQuoteIcon />
            </IconButton>
            </Tooltip>
            
            <Tooltip title="Inline Code (`text`)">
            <IconButton onClick={() => applyFormatting('inline-code')}>
                <CodeIcon fontSize="small" />
            </IconButton>
            </Tooltip>
            
            <Divider orientation="vertical" flexItem style={{ margin: '0 8px' }} />
            
            <Tooltip title="Undo">
            <IconButton 
                onClick={handleUndo}
                disabled={historyIndex === 0}
            >
                <UndoIcon />
            </IconButton>
            </Tooltip>
            
            <Tooltip title="Emoji">
            <IconButton onClick={() => setShowEmojiPicker(!showEmojiPicker)}>
                <EmojiEmotionsIcon />
            </IconButton>
            </Tooltip>

            <Tooltip 
                title={
                !hasEnhancePermission 
                    ? "Feature is not available in your current plan" 
                    : "Enhance Text"
                }
            >
            <IconButton 
                onClick={handleClickEnhance}
                disabled={!value}
            >
                <AutoFixHighIcon />
            </IconButton>
            </Tooltip>
            
            <div style={{ marginLeft: 'auto' }}>
            <Typography variant="body2" color={text.length > maxLength * 0.9 ? 'error' : 'textSecondary'}>
                {text.length}/{maxLength}
            </Typography>
            </div>
        </div>
        
        {/* Editor and Preview Layout */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            {/* Text Editor */}
            <textarea
                ref={textareaRef}
                value={text}
                onChange={handleChange}
                onKeyDown={handleKeyDownInternal}
                style={textAreaStyle}
                spellCheck={true}
                disabled={disabled}
            />
        </div>

        {/* Enhanced Text with Animation */}
        {showEnhanced && (
            <div style={{
                marginTop: '16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
            }}>
                <div style={{
                    padding: '8px',
                    backgroundColor: '#f0f7ff',
                    borderBottom: '1px solid #ddd',
                    display: 'flex',
                    alignItems: 'center'
                }}>
                    <AutoFixHighIcon fontSize="small" style={{ marginRight: '8px', color: '#1976d2' }} />
                    <Typography variant="subtitle2">Enhanced Text ({enhanceOption})</Typography>
                </div>
                
                {/* Typing Animation container */}
                <div style={{
                    width: '100%',
                    height: '120px',
                    padding: '12px',
                    fontFamily: 'monospace', // Match textarea
                    fontSize: '14px', // Match textarea
                    lineHeight: '1.5', // Match textarea
                    backgroundColor: '#fafafa',
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap' // Match textarea
                }}>
                    {isEnhancing ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <CircularProgress size={30} />
                        </div>
                    ) : (
                        <TypeAnimation
                            splitter={(str) => str.split(/(?= )/)} // Split by spaces but keep spaces with the following word
                            sequence={[
                                enhancedText,
                                1000, // Wait 1 second after typing
                                () => handleAnimationComplete(), // Callback when animation is complete
                            ]}
                            speed={{ type: 'keyStrokeDelayInMs', value: 30 }}
                            omitDeletionAnimation={true}
                            style={{
                                fontSize: '14px', // Match textarea
                                fontFamily: 'monospace', // Match textarea
                                display: 'block',
                                minHeight: '100px',
                                lineHeight: '1.5', // Match textarea
                                whiteSpace: 'pre-wrap', // Match textarea
                                color: 'inherit' // Use regular text color
                            }}
                        />
                    )}
                </div>
                
                <div style={{
                    padding: '8px',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    borderTop: '1px solid #ddd',
                    backgroundColor: '#f5f5f5'
                }}>
                    <Button 
                        variant="text"
                        onClick={handleCancelEnhancement}
                        style={{ marginRight: '8px' }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="contained" 
                        color="primary"
                        onClick={() => handleReplaceWithEnhanced(maxLength - 4)}
                        disabled={isEnhancing || !animationComplete} // Disable until animation completes
                    >
                        Replace
                    </Button>
                </div>
            </div>
        )}
    
        {/* Enhancement Options Menu */}
        <Menu
            anchorEl={enhanceMenuAnchor}
            open={Boolean(enhanceMenuAnchor)}
            onClose={handleCloseEnhanceMenu}
        >
            <MenuItem onClick={() => handleSelectEnhanceOption('professional')}>
                <ListItemIcon><BusinessIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Professional" />
            </MenuItem>
            <MenuItem onClick={() => handleSelectEnhanceOption('neutral')}>
                <ListItemIcon><RemoveIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Neutral" />
            </MenuItem>
            <MenuItem onClick={() => handleSelectEnhanceOption('friendly')}>
                <ListItemIcon><SentimentSatisfiedAltIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Friendly" />
            </MenuItem>
            <MenuItem onClick={() => handleSelectEnhanceOption('humorous')}>
                <ListItemIcon><EmojiEmotionsIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Humorous" />
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => handleSelectEnhanceOption('grammar_check')}>
                <ListItemIcon><SpellcheckIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Grammar & Spell Check" />
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => handleSelectEnhanceOption('shorten')}>
                <ListItemIcon><ShortTextIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Make it Short" />
            </MenuItem>
            <MenuItem onClick={() => handleSelectEnhanceOption('lengthen')}>
                <ListItemIcon><SubjectIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Make it Longer" />
            </MenuItem>
            <MenuItem onClick={() => handleSelectEnhanceOption('simplify')}>
                <ListItemIcon><TranslateIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Make it Simple" />
            </MenuItem>
        </Menu>
        
        {/* Emoji Picker */}
        {showEmojiPicker && (
            <div style={{ 
                position: 'absolute',
                zIndex: 1000,
                marginTop: '8px',
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                border: '1px solid #ddd',
                borderRadius: '4px'
            }}>
                <Picker 
                    onEmojiClick={onEmojiClick}
                    lazyLoadEmojis={true}
                />
            </div>
        )}
        </div>
    );
};

export default CustomMarkdownEditor;